import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Check, Plus, X } from 'lucide-react';
import clsx from 'clsx';
import type { LeaveDto } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { TextAreaField } from '../components/ui/Field';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { LeaveDrawer } from '../components/calendar/LeaveDrawer';
import { LEAVE_TYPE_LABEL, LeaveStatusChip } from '../components/calendar/LeaveStatusChip';
import { academyToday, dayCount, formatRange } from '../components/calendar/format';

export const Route = createFileRoute('/_authed/academics/leave')({
  component: LeaveApprovalsPage,
});

type Tab = 'pending' | 'upcoming' | 'all';
const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'pending', label: 'Awaiting approval' },
  { value: 'upcoming', label: 'Approved & upcoming' },
  { value: 'all', label: 'All' },
];

function LeaveApprovalsPage() {
  const canDecide = useAuthStore((s) => s.hasRole('super_admin', 'academic_admin', 'nalim'));
  if (!canDecide) {
    return (
      <ListShell title="Teacher leave">
        <EmptyState
          title="Not available for your role"
          description="Nalims and academic admins review teacher leave here."
          icon={<CalendarOff size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }
  return <LeaveApprovals />;
}

function LeaveApprovals() {
  const [tab, setTab] = useState<Tab>('pending');
  const [recordOpen, setRecordOpen] = useState(false);
  const [rejecting, setRejecting] = useState<LeaveDto | null>(null);
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const today = academyToday();

  const leavesQuery = useQuery({
    queryKey: ['calendar', 'leaves', 'review'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: LeaveDto[] }>('/api/calendar/leaves', { signal, query: { from: addMonths(today, -3) } }),
  });
  // Only the teachers the caller may record leave for: everyone for admins,
  // the teachers of their own batches for a nalim.
  const teachersQuery = useQuery({
    queryKey: ['calendar', 'leave-teachers'] as const,
    enabled: recordOpen,
    queryFn: ({ signal }) => apiRequest<{ items: Array<{ id: string; name: string }> }>('/api/calendar/leave-teachers', { signal }),
    staleTime: 60_000,
  });
  const recordTeachers = teachersQuery.data?.items ?? [];

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['calendar'] });
  const onError = (fallback: string) => (err: unknown) =>
    toast(err instanceof ApiError ? err.message : fallback, { variant: 'error' });

  const approve = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: true }>(`/api/calendar/leaves/${encodeURIComponent(id)}/approve`, { method: 'POST', body: {} }),
    onSuccess: () => {
      toast('Leave approved — the teacher has been notified.', { variant: 'success' });
      invalidate();
    },
    onError: onError('Could not approve leave.'),
  });
  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiRequest<{ ok: true }>(`/api/calendar/leaves/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        body: { note: note.trim() ? note.trim() : null },
      }),
    onSuccess: () => {
      toast('Leave not approved — the teacher has been notified.', { variant: 'success' });
      setRejecting(null);
      invalidate();
    },
    onError: onError('Could not update leave.'),
  });
  const cancel = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: true }>(`/api/calendar/leaves/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} }),
    onSuccess: () => {
      toast('Leave cancelled.', { variant: 'success' });
      invalidate();
    },
    onError: onError('Could not cancel leave.'),
  });

  const all = useMemo(() => leavesQuery.data?.items ?? [], [leavesQuery.data]);
  const counts = {
    pending: all.filter((l) => l.status === 'requested').length,
    upcoming: all.filter((l) => l.status === 'approved' && l.endDate >= today).length,
    all: all.length,
  };
  const visible =
    tab === 'pending'
      ? all.filter((l) => l.status === 'requested')
      : tab === 'upcoming'
        ? all.filter((l) => l.status === 'approved' && l.endDate >= today)
        : [...all].reverse();

  return (
    <ListShell
      title="Teacher leave"
      description="Approve leave requests and record leave for your teachers. Approved leave removes those classes from attendance reminders and teacher reports."
      primaryAction={
        <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setRecordOpen(true)}>
          Record leave
        </Button>
      }
    >
      <div role="tablist" aria-label="Leave status" className="flex gap-1 overflow-x-auto border-b border-[color:var(--color-border)]">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            onClick={() => setTab(t.value)}
            className={clsx(
              '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm',
              tab === t.value
                ? 'border-gold-500 font-semibold text-[color:var(--color-text)]'
                : 'border-transparent text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
            )}
          >
            {t.label}
            {counts[t.value] > 0 ? (
              <span className="ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[color:var(--color-surface-strong)] px-1.5 text-[10px] font-semibold tabular-nums">
                {counts[t.value]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {leavesQuery.isLoading ? (
        <div className="grid gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : leavesQuery.isError ? (
        <ErrorState
          title="Could not load leave"
          description={leavesQuery.error instanceof ApiError ? leavesQuery.error.message : undefined}
          onRetry={() => void leavesQuery.refetch()}
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title={tab === 'pending' ? 'No requests waiting' : 'Nothing here'}
          description={tab === 'pending' ? 'New leave requests from your teachers will appear here.' : undefined}
          icon={<CalendarOff size={32} aria-hidden="true" />}
        />
      ) : (
        <ul className="divide-y divide-dashed divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
          {visible.map((l) => (
            <li key={l.id} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <p className="font-semibold">{l.teacherName}</p>
                <p className="text-sm">
                  {formatRange(l.startDate, l.endDate)}{' '}
                  <span className="text-[color:var(--color-text-muted)]">
                    · {dayCount(l.startDate, l.endDate)} day{dayCount(l.startDate, l.endDate) === 1 ? '' : 's'} ·{' '}
                    {LEAVE_TYPE_LABEL[l.leaveType]}
                  </span>
                </p>
                {l.reason ? <p className="text-sm text-[color:var(--color-text-muted)]">“{l.reason}”</p> : null}
                {l.decisionNote ? (
                  <p className="text-xs text-[color:var(--color-text-muted)]">Note: {l.decisionNote}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <LeaveStatusChip status={l.status} />
                {l.status === 'requested' ? (
                  <>
                    <Button size="sm" variant="secondary" icon={<X size={14} aria-hidden="true" />} onClick={() => setRejecting(l)}>
                      Decline
                    </Button>
                    <Button
                      size="sm"
                      icon={<Check size={14} aria-hidden="true" />}
                      loading={approve.isPending && approve.variables === l.id}
                      onClick={() => approve.mutate(l.id)}
                    >
                      Approve
                    </Button>
                  </>
                ) : l.status === 'approved' && l.endDate >= today ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Cancel ${l.teacherName}’s leave?`,
                        description: `${formatRange(l.startDate, l.endDate)} · their classes will be expected again.`,
                        confirmLabel: 'Cancel leave',
                        cancelLabel: 'Keep it',
                        variant: 'danger',
                      });
                      if (ok) cancel.mutate(l.id);
                    }}
                  >
                    Cancel leave
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <LeaveDrawer
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        mode="record"
        teachers={recordTeachers}
      />
      <DeclineDrawer
        leave={rejecting}
        onClose={() => setRejecting(null)}
        onConfirm={(note) => rejecting && reject.mutate({ id: rejecting.id, note })}
        submitting={reject.isPending}
      />
    </ListShell>
  );
}

function DeclineDrawer({
  leave,
  onClose,
  onConfirm,
  submitting,
}: {
  leave: LeaveDto | null;
  onClose: () => void;
  onConfirm: (note: string) => void;
  submitting: boolean;
}) {
  return (
    <Drawer open={leave !== null} onClose={onClose} title="Decline leave request" size="sm">
      {/* Keyed per leave so a note typed for one request doesn't carry over to the next. */}
      {leave ? <DeclineForm key={leave.id} leave={leave} onClose={onClose} onConfirm={onConfirm} submitting={submitting} /> : null}
    </Drawer>
  );
}

function DeclineForm({
  leave,
  onClose,
  onConfirm,
  submitting,
}: {
  leave: LeaveDto;
  onClose: () => void;
  onConfirm: (note: string) => void;
  submitting: boolean;
}) {
  const [note, setNote] = useState('');
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onConfirm(note);
      }}
    >
      <p className="text-sm">
        <span className="font-semibold">{leave.teacherName}</span> · {formatRange(leave.startDate, leave.endDate)}
      </p>
      <TextAreaField
        label="Note for the teacher"
        optional
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        hint="e.g. “Exam week — can we move this to the week after?”"
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="danger" loading={submitting}>
          Decline request
        </Button>
      </div>
    </form>
  );
}

const addMonths = (date: string, delta: number): string => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + delta);
  return d.toISOString().slice(0, 10);
};
